import { circle } from './draw-utils';
import { glow, lumpPath, makeSheet } from './env-kit';

/**
 * Floor decals per sector theme (6 variants each, docs/04). FLAT by rule:
 * no outlines, no highlights, low alpha — they must never read as solid
 * cover or as pickups. Baked into cell textures (arena-view.ts).
 */
const SIZE = 128;
const FRAMES = 6;

function stroke(ctx: CanvasRenderingContext2D, color: string, width: number): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
}

function crackLine(ctx: CanvasRenderingContext2D, c: number, rnd: () => number, len: number): void {
  ctx.beginPath();
  let x = c - len / 2;
  let y = c + (rnd() - 0.5) * 20;
  ctx.moveTo(x, y);
  for (let i = 0; i < 5; i++) {
    x += len / 5;
    y += (rnd() - 0.5) * 26;
    ctx.lineTo(x, y);
  }
}

export const envDecalScrap = makeSheet('env/decal-scrap', SIZE, FRAMES, (ctx, c, f, rnd) => {
  switch (f) {
    case 0: // oil stain
      lumpPath(ctx, c, c, c * 0.62, 11, 0.18, 0.6);
      ctx.fillStyle = 'rgba(10, 12, 16, 0.4)';
      ctx.fill();
      break;
    case 1: // patch plate
      ctx.fillStyle = 'rgba(90, 108, 116, 0.35)';
      ctx.fillRect(c - 34, c - 26, 68, 52);
      ctx.fillStyle = 'rgba(140, 160, 168, 0.35)';
      for (const [x, y] of [[-28, -20], [28, -20], [-28, 20], [28, 20]] as const) {
        circle(ctx, c + x, c + y, 2.5);
        ctx.fill();
      }
      break;
    case 2: // hazard stripes
      ctx.save();
      ctx.beginPath();
      ctx.rect(c - 44, c - 14, 88, 28);
      ctx.clip();
      for (let i = -6; i < 8; i++) {
        ctx.fillStyle = i % 2 === 0 ? 'rgba(245, 166, 35, 0.32)' : 'rgba(20, 16, 31, 0.3)';
        ctx.beginPath();
        ctx.moveTo(c - 44 + i * 14, c + 14);
        ctx.lineTo(c - 30 + i * 14, c - 14);
        ctx.lineTo(c - 16 + i * 14, c - 14);
        ctx.lineTo(c - 30 + i * 14, c + 14);
        ctx.fill();
      }
      ctx.restore();
      break;
    case 3: // grate
      ctx.fillStyle = 'rgba(12, 16, 20, 0.45)';
      ctx.fillRect(c - 30, c - 30, 60, 60);
      ctx.fillStyle = 'rgba(80, 96, 104, 0.4)';
      for (let i = 0; i < 6; i++) ctx.fillRect(c - 28 + i * 10, c - 28, 4, 56);
      break;
    case 4: // scorch
      glow(ctx, c, c, c * 0.7, '8, 8, 10', 0.5);
      break;
    default: // bolts scatter
      ctx.fillStyle = 'rgba(130, 150, 158, 0.4)';
      for (let i = 0; i < 7; i++) {
        circle(ctx, c + (rnd() - 0.5) * 70, c + (rnd() - 0.5) * 70, 2 + rnd() * 2);
        ctx.fill();
      }
  }
});

export const envDecalCore = makeSheet('env/decal-core', SIZE, FRAMES, (ctx, c, f, rnd) => {
  switch (f) {
    case 0: // scorch ring
      glow(ctx, c, c, c * 0.75, '10, 4, 6', 0.55);
      break;
    case 1: // glowing crack
      crackLine(ctx, c, rnd, 100);
      stroke(ctx, 'rgba(255, 122, 48, 0.35)', 7);
      stroke(ctx, 'rgba(255, 220, 140, 0.45)', 2);
      break;
    case 2: // cinders
      for (let i = 0; i < 9; i++) {
        circle(ctx, c + (rnd() - 0.5) * 80, c + (rnd() - 0.5) * 80, 1.5 + rnd() * 2);
        ctx.fillStyle = i % 3 === 0 ? 'rgba(255, 140, 60, 0.55)' : 'rgba(20, 10, 12, 0.5)';
        ctx.fill();
      }
      break;
    case 3: // forked vein
      ctx.beginPath();
      ctx.moveTo(c - 40, c + 30); ctx.lineTo(c, c); ctx.lineTo(c + 40, c + 26);
      ctx.moveTo(c, c); ctx.lineTo(c + 6, c - 42);
      stroke(ctx, 'rgba(255, 110, 40, 0.3)', 6);
      break;
    case 4: // ash patch
      lumpPath(ctx, c, c, c * 0.55, 10, 0.2, 1.3);
      ctx.fillStyle = 'rgba(90, 74, 78, 0.3)';
      ctx.fill();
      break;
    default: // rune circle
      ctx.beginPath();
      ctx.arc(c, c, c * 0.6, 0, Math.PI * 2);
      stroke(ctx, 'rgba(255, 150, 70, 0.22)', 3);
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        circle(ctx, c + Math.cos(a) * c * 0.6, c + Math.sin(a) * c * 0.6, 4);
        ctx.fillStyle = 'rgba(255, 170, 90, 0.3)';
        ctx.fill();
      }
  }
});

export const envDecalBrood = makeSheet('env/decal-brood', SIZE, FRAMES, (ctx, c, f, rnd) => {
  switch (f) {
    case 0: // slime puddle
      lumpPath(ctx, c, c, c * 0.6, 12, 0.15, 0.2);
      ctx.fillStyle = 'rgba(150, 220, 90, 0.18)';
      ctx.fill();
      break;
    case 1: // vein web
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i + rnd() * 0.6;
        ctx.beginPath();
        ctx.moveTo(c, c);
        ctx.quadraticCurveTo(c + Math.cos(a + 0.5) * 30, c + Math.sin(a + 0.5) * 30,
          c + Math.cos(a) * 56, c + Math.sin(a) * 56);
        stroke(ctx, 'rgba(160, 70, 140, 0.3)', 3);
      }
      break;
    case 2: // spore spots
      for (let i = 0; i < 8; i++) {
        circle(ctx, c + (rnd() - 0.5) * 80, c + (rnd() - 0.5) * 80, 3 + rnd() * 4);
        ctx.fillStyle = 'rgba(190, 240, 120, 0.22)';
        ctx.fill();
      }
      break;
    case 3: // membrane scar
      ctx.beginPath();
      ctx.ellipse(c, c, 46, 16, 0.4, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(14, 22, 12, 0.45)';
      ctx.fill();
      break;
    case 4: // flat egg prints
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.ellipse(c + (i - 1.5) * 18, c + (i % 2) * 14 - 7, 9, 11, 0.3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(170, 210, 120, 0.2)';
        ctx.fill();
      }
      break;
    default: // acid drip
      glow(ctx, c, c, c * 0.45, '160, 255, 90', 0.22);
  }
});

export const envDecalRim = makeSheet('env/decal-rim', SIZE, FRAMES, (ctx, c, f, rnd) => {
  switch (f) {
    case 0: // glyph circle
      ctx.beginPath();
      ctx.arc(c, c, c * 0.62, 0, Math.PI * 2);
      stroke(ctx, 'rgba(176, 124, 255, 0.3)', 3);
      ctx.beginPath();
      for (let i = 0; i <= 3; i++) {
        const a = (Math.PI * 2 * i) / 3 - Math.PI / 2;
        if (i === 0) ctx.moveTo(c + Math.cos(a) * 36, c + Math.sin(a) * 36);
        else ctx.lineTo(c + Math.cos(a) * 36, c + Math.sin(a) * 36);
      }
      stroke(ctx, 'rgba(200, 160, 255, 0.28)', 2);
      break;
    case 1: // rift crack
      crackLine(ctx, c, rnd, 104);
      stroke(ctx, 'rgba(176, 124, 255, 0.32)', 6);
      stroke(ctx, 'rgba(240, 225, 255, 0.4)', 1.5);
      break;
    case 2: // star cluster
      for (let i = 0; i < 9; i++) {
        circle(ctx, c + (rnd() - 0.5) * 84, c + (rnd() - 0.5) * 84, 1 + rnd() * 1.6);
        ctx.fillStyle = 'rgba(230, 215, 255, 0.55)';
        ctx.fill();
      }
      break;
    case 3: // glowing hex tile
      ctx.beginPath();
      for (let i = 0; i <= 6; i++) {
        const a = (Math.PI / 3) * i;
        if (i === 0) ctx.moveTo(c + Math.cos(a) * 32, c + Math.sin(a) * 32);
        else ctx.lineTo(c + Math.cos(a) * 32, c + Math.sin(a) * 32);
      }
      ctx.fillStyle = 'rgba(120, 90, 210, 0.16)';
      ctx.fill();
      stroke(ctx, 'rgba(176, 124, 255, 0.25)', 2);
      break;
    case 4: // shard dust
      for (let i = 0; i < 6; i++) {
        ctx.save();
        ctx.translate(c + (rnd() - 0.5) * 70, c + (rnd() - 0.5) * 70);
        ctx.rotate(rnd() * Math.PI);
        ctx.fillStyle = 'rgba(160, 130, 230, 0.3)';
        ctx.fillRect(-5, -2, 10, 4);
        ctx.restore();
      }
      break;
    default: // void stain
      glow(ctx, c, c, c * 0.7, '4, 2, 12', 0.5);
  }
});
